import React from 'react';
import Table, { TableColumn } from '../../components/common/Table';
import { ListingDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { getBadgeColors } from '../../utils/displayUtils';
import Badge from '../common/Badge';
import FileIcons from '../common/FileIcons';
import ReviewPanel from '../reviewPanels/ReviewPanel';

interface ListingTableProps {
  data: ListingDTO[];
  handleEdit: (row: ListingDTO) => void;
  handleDelete: (row: ListingDTO) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
}

const ListingTable: React.FC<ListingTableProps> = ({
  data,
  handleEdit,
  handleDelete,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
}) => {
  const columns: TableColumn[] = [
    {
      label: 'Type',
      key: 'listingTypeId',
      sortable: true,
      sortKey: 'ListingTypeTitle',
      showInReview: true,
      render: (row: ListingDTO) => (
        <span>{row.listingType?.title ?? 'Unknown'}</span>
      ),
    },

    {
      label: 'Name',
      key: 'name',
      sortable: true,
      sortKey: 'Name',
      showInReview: true,
    },
    {
      label: 'Town',
      key: 'closestCity',
      sortable: true,
      sortKey: 'ClosestCity',
      showInReview: true,
    },
    {
      label: 'Responsible',
      key: 'responsible',
      sortable: true,
      sortKey: 'ResponsiblePersonName',
      showInReview: true,
      render: (row: ListingDTO) => (
        <span>
          {row.responsiblePersonName ?? ''}{' '}
          {row.responsiblePersonSurname ?? ''}
        </span>
      ),
    },
    {
      label: 'Files',
      key: 'listingFiles',
      render: (row: ListingDTO) => (
        <FileIcons
          files={row.listingFiles?.map((listingFile) => ({
            id: listingFile.fileId,
            label: listingFile.file?.fileName || ''
          })) || []}
          renderSidePanel={() => <ReviewPanel row={row} columns={columns} />}
        />
      ),
    },
    {
      label: 'Status',
      key: 'listingStatusId',
      sortable: true,
      sortKey: 'ListingStatusTitle',
      showInReview: true,
      render: (row: ListingDTO) => {
        const base = row.listingStatus?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.40);
        return base ? (
          <Badge
            label={row.listingStatus?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.listingStatus?.title || 'Unknown'}</span>
        );
      },
    },
    {
      label: 'Added',
      key: 'createdUtc',
      sortable: true,
      sortKey: 'CreatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },
  ];

  return (
    <Table
      columns={columns}
      data={data}
      actions={{
        onEdit: handleEdit,
        onDelete: handleDelete,
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default ListingTable;
